/**
 * Sprint V100 — Ergebnis-Anzeige: Primary + Secondary Fokustyp.
 * Glyphen NUR hier, nicht in den Fragen.
 *
 * V100.3 (17.09.2026) — Follow-up-Sektion kontextsensitiv:
 *   - LP + /quickcheck (Cold-Traffic): Free-Registration-CTA via onMagicLink
 *   - /dashboard/test (eingeloggt): „Zurueck zum Cockpit"-CTA
 *   - Der „Zum 20-Fragen-Test" verlinkte frueher auf /pionier-quick-check und
 *     hat User in den Pionier-Warteliste-Flow geworfen → das war Nonsense
 *     fuer LP-Traffic. Entfernt.
 *
 * V99 A/B-Test-Anschluss:
 *   variant === "A" → Video-Bridge unter dem Ergebnis
 *   variant === "B" → Direkter CTA ohne Video
 *   Beide Varianten mit data-testid-Marker fuer Regressions-Guards.
 */
import React from "react";
import { ArrowRight, Sparkles, HeartHandshake, Lightbulb, Check } from "lucide-react";
import VideoBridge from "@/components/VideoBridge";
import { FOKUSTYP_NAMES, FOKUSTYP_ICONS, FOKUSTYP_TAGLINES } from "../../data/quickCheckV100Data";
import {
  SPIEGEL_BY_FOKUSTYP,
  PAIN_QUESTION_BY_FOKUSTYP,
  QUICK_TIP_BY_FOKUSTYP,
  FREE_COCKPIT_FEATURES,
} from "../../data/quickCheckValueBridge";

/**
 * Sprint V100+ (18.09.2026) — Value-Bridge-Karten (Spiegel · Pain · Quick-Tip).
 * Drei kompakte Slots ZWISCHEN Ergebnis-Header und Registrierungs-CTA.
 * Ziel: Der User bekommt Value BEVOR er nach seiner E-Mail gefragt wird.
 * Balance: 1 Satz + 1 Frage + 1 Werkzeug — mehr steckt im Free-Cockpit.
 */
function ValueBridgeCards({ primaryType }) {
  const spiegel = SPIEGEL_BY_FOKUSTYP[primaryType];
  const pain = PAIN_QUESTION_BY_FOKUSTYP[primaryType];
  const tip = QUICK_TIP_BY_FOKUSTYP[primaryType];
  if (!spiegel && !pain && !tip) return null;
  return (
    <div className="mt-10 space-y-4 text-left" data-testid="v100-result-value-bridge">
      {spiegel && (
        <div
          className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-5 sm:p-6 shadow-sm"
          data-testid="value-bridge-spiegel"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#4563BB]/10 text-[#4563BB] flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold uppercase tracking-widest text-[#4563BB] mb-1">
                Der Spiegel
              </p>
              <p className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white leading-relaxed">
                {spiegel}
              </p>
            </div>
          </div>
        </div>
      )}
      {pain && (
        <div
          className="rounded-2xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 p-5 sm:p-6"
          data-testid="value-bridge-pain"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#F3651D]/10 text-[#F3651D] flex items-center justify-center">
              <HeartHandshake className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold uppercase tracking-widest text-[#F3651D] mb-1">
                Kennst du das?
              </p>
              <p className="text-sm sm:text-base text-slate-700 dark:text-slate-200 leading-relaxed">
                {pain}
              </p>
            </div>
          </div>
        </div>
      )}
      {tip && (
        <div
          className="rounded-2xl bg-gradient-to-br from-amber-50 to-white dark:from-amber-950/30 dark:to-slate-800 border border-amber-200 dark:border-amber-800/50 p-5 sm:p-6"
          data-testid="value-bridge-tip"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-400/20 text-amber-700 dark:text-amber-300 flex items-center justify-center">
              <Lightbulb className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold uppercase tracking-widest text-amber-700 dark:text-amber-300 mb-1">
                Ein Werkzeug, das du morgen probieren kannst
              </p>
              <p className="text-sm sm:text-base text-slate-800 dark:text-slate-100 leading-relaxed">
                {tip}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FollowUpBlock({ onMagicLink, primaryType, onBackToCockpit, mode, cockpitCtaLabel }) {
  // Mode „dashboard": User ist eingeloggt, kurzer Rueckweg zum Cockpit.
  if (mode === "dashboard") {
    return (
      <div
        className="mt-10 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 p-6 text-center"
        data-testid="v100-result-followup-dashboard"
      >
        <p className="text-sm sm:text-base text-slate-700 dark:text-slate-200">
          Dein Fokustyp ist in deinem Profil gespeichert. Weiter geht&apos;s im Mitgliederbereich.
        </p>
        <button
          type="button"
          onClick={onBackToCockpit}
          data-testid="v100-result-back-to-cockpit"
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-3 text-sm font-bold hover:opacity-90 transition"
        >
          {cockpitCtaLabel || "Zurück zum Mitgliederbereich"} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Mode „landing": Cold-Traffic → Free-Registration-CTA mit Value-Framing.
  // V100+ (18.09.2026) — Feature-Liste eingebaut, damit klar ist WAS der User
  // im Cockpit bekommt. Das schließt die Value-Bridge (Spiegel/Pain/Tip)
  // auf eine „Willst du mehr davon?"-Weise ab.
  return (
    <div
      className="mt-10 rounded-2xl bg-gradient-to-br from-[#F3651D]/10 via-white to-[#4563BB]/10 dark:from-[#F3651D]/20 dark:via-slate-800 dark:to-[#4563BB]/20 border border-[#F3651D]/30 dark:border-[#F3651D]/40 p-6 sm:p-8 text-left"
      data-testid="v100-result-followup-landing"
    >
      <p className="text-xs font-bold uppercase tracking-widest text-[#F3651D] text-center">
        Kostenfrei · Dein nächster Schritt
      </p>
      <h3 className="mt-2 text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white text-center">
        Dein persönlicher PocketCoach wartet.
      </h3>
      {/* Sprint V101.g (20.09.2026) — Micro-Explainer (Option D): eine Zeile,
          die klarstellt „das ist eine echte App, nicht nur ein PDF-Download".
          Absichtlich schlank, damit die Value-Bridge nicht überladen wird. */}
      <p
        className="mt-3 text-sm text-slate-600 dark:text-slate-300 leading-relaxed text-center max-w-lg mx-auto"
        data-testid="v100-result-app-explainer"
      >
        Wir richten dir deinen persönlichen PocketCoach ein —
        <span className="font-semibold text-slate-800 dark:text-slate-100"> kostenlos, direkt im Browser, kein Download</span>.
        Dein Fokustyp-Ergebnis bleibt dort dauerhaft gespeichert.
      </p>
      <p className="mt-4 text-sm sm:text-base text-slate-700 dark:text-slate-200 leading-relaxed text-center">
        Was dich im PocketCoach erwartet:
      </p>

      {/* Sprint V101.g (20.09.2026, Option B) — Cockpit-Preview-Kachel.
          Echter Screenshot als visueller Anker: „Das ist eine echte App,
          kein PDF-Download." Gerahmt wie ein Browser-Fenster, um die
          Web-App-Assoziation weiter zu verstärken. */}
      <figure
        className="mt-5 max-w-xl mx-auto rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-lg bg-white"
        data-testid="v100-result-cockpit-preview"
      >
        <div className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          <span className="ml-3 text-[10px] font-mono text-slate-500 truncate">
            flex4success.effektiv-kommunizieren.de/cockpit
          </span>
        </div>
        <img
          src="/images/cockpit-preview.jpg"
          alt="So sieht dein persönlicher PocketCoach gleich aus — Übersicht, Fokustyp-Steckbrief, weitere Module in der Seitenleiste."
          className="w-full block"
          loading="lazy"
        />
        <figcaption className="px-3 py-2 text-[11px] text-slate-500 text-center bg-slate-50 dark:bg-slate-800 border-t border-slate-100 dark:border-slate-700">
          So sieht dein persönlicher PocketCoach gleich aus.
        </figcaption>
      </figure>
      <ul
        className="mt-4 space-y-2 max-w-lg mx-auto"
        data-testid="v100-result-features-list"
      >
        {FREE_COCKPIT_FEATURES.map((feat) => (
          <li key={feat} className="flex items-start gap-2.5">
            <Check className="w-5 h-5 flex-shrink-0 text-emerald-600 dark:text-emerald-400 mt-0.5" />
            <span className="text-sm sm:text-base text-slate-800 dark:text-slate-100 leading-relaxed">
              {feat}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-6 text-center">
        <button
          type="button"
          onClick={() =>
            onMagicLink && onMagicLink("quickcheck_v100_result", primaryType)
          }
          data-testid="v100-result-free-signup"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#F3651D] hover:bg-[#d95415] text-white px-6 sm:px-8 py-3 sm:py-3.5 text-sm sm:text-base font-bold shadow-md hover:shadow-lg transition-all"
        >
          Zugang zu deinem PocketCoach holen <ArrowRight className="w-4 h-4" />
        </button>
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
          Ein Klick auf deinen Magic-Link genügt · Kein Passwort · Jederzeit kündbar
        </p>
      </div>
    </div>
  );
}

export default function QuickCheckResult({
  primaryType,
  secondaryType,
  scores,
  onRestart,
  variant,
  onMagicLink,
  onBackToCockpit,
  followUpMode,      // "landing" | "dashboard"
  cockpitCtaLabel,
  showTypeVideo,
}) {
  const primaryName = FOKUSTYP_NAMES[primaryType] || primaryType;
  const primaryIcon = FOKUSTYP_ICONS[primaryType];
  const primaryTagline = FOKUSTYP_TAGLINES[primaryType];

  const secondaryName = FOKUSTYP_NAMES[secondaryType] || secondaryType;
  const secondaryIcon = FOKUSTYP_ICONS[secondaryType];

  return (
    <div className="max-w-3xl mx-auto text-center" data-testid="v100-result">
      <div className="inline-flex items-center gap-2 rounded-full bg-[#4563BB]/10 text-[#4563BB] px-4 py-1.5 text-xs font-bold uppercase tracking-widest">
        Dein Ergebnis
      </div>

      {/* Primary */}
      <div className="mt-6" data-testid="v100-result-primary">
        <img
          src={primaryIcon}
          alt={primaryName}
          className="mx-auto w-24 h-24 sm:w-32 sm:h-32 object-contain"
        />
        <h2
          className="mt-4 text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white"
          data-testid="v100-result-primary-name"
        >
          {primaryName}
        </h2>
        <p className="mt-2 text-base sm:text-lg text-slate-600 dark:text-slate-300">
          {primaryTagline}
        </p>
      </div>

      {/* Secondary — nur wenn vorhanden (kein Zweittyp mit 0 Punkten) */}
      {secondaryType && <div
        className="mt-8 mx-auto max-w-md rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-5 flex items-center gap-4 text-left"
        data-testid="v100-result-secondary"
      >
        <img
          src={secondaryIcon}
          alt={secondaryName}
          className="w-12 h-12 sm:w-16 sm:h-16 flex-shrink-0 object-contain"
        />
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
            Du hast auch etwas von
          </div>
          <div
            className="text-lg font-bold text-slate-900 dark:text-white"
            data-testid="v100-result-secondary-name"
          >
            {secondaryName}
          </div>
        </div>
      </div>}

      {/* Score-Balken */}
      <div className="mt-8 max-w-md mx-auto space-y-2" data-testid="v100-result-scores">
        {["katze", "eule", "biene", "hund"].map((k) => {
          const val = scores?.[k] || 0;
          const pct = Math.min(100, (val / 8) * 100);
          return (
            <div key={k} className="flex items-center gap-3 text-sm">
              <div className="w-14 text-right text-slate-500 dark:text-slate-400">
                {FOKUSTYP_NAMES[k]}
              </div>
              <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                <div
                  className={`h-full rounded-full ${k === primaryType ? "bg-[#4563BB]" : "bg-slate-300 dark:bg-slate-500"}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="w-6 text-left text-slate-700 dark:text-slate-200 font-semibold">
                {val}
              </div>
            </div>
          );
        })}
      </div>

      {showTypeVideo && (
        <div className="mt-10" data-testid="v100-result-type-video">
          <VideoBridge fokustyp={primaryType} hideCtaForUser />
        </div>
      )}

      {/* Sprint V100+ (18.09.2026) — Value-Bridge: Spiegel + Pain + Quick-Tip.
          Nur im Landing-Mode (Cold-Traffic). Im Dashboard-Mode hat der User
          schon Zugang zum vollen Content im Cockpit. */}
      {followUpMode !== "dashboard" && (
        <ValueBridgeCards primaryType={primaryType} />
      )}

      {/* V99 A/B-Test — Video-Bridge nur in Variante A (unter der Value-Bridge) */}
      {variant === "A" && (
        <div className="mt-10" data-testid="ab-v99-variant-a">
          <VideoBridge fokustyp={primaryType} />
        </div>
      )}

      {variant === "B" && (
        <div className="mt-10" data-testid="ab-v99-variant-b" />
      )}

      {/* Follow-up — kontextsensitiv */}
      <FollowUpBlock
        onMagicLink={onMagicLink}
        primaryType={primaryType}
        onBackToCockpit={onBackToCockpit}
        mode={followUpMode || "landing"}
        cockpitCtaLabel={cockpitCtaLabel}
      />

      {onRestart && followUpMode !== "dashboard" && (
        <button
          type="button"
          onClick={onRestart}
          data-testid="v100-result-restart"
          className="mt-6 text-sm text-slate-500 dark:text-slate-400 hover:underline"
        >
          Nochmal machen
        </button>
      )}
    </div>
  );
}
